<template>
  <div class="crawl-form flex-grow-1 d-flex flex-column overflow-y-hidden">
    <a-form id="crawl-form" class="flex-grow-1 d-flex flex-column overflow-y-hidden" layout="vertical" @submit.prevent="onSubmit">
      <div class="a-header">
        <span class="a-header--title slide-down-enter-active">Start Website Crawl</span>
      </div>
      <div class="a-body p-20-px flex-grow-1 overflow-y-auto">
        <transition name="slide-up">
          <a-row :gutter="24">
            <a-col :span="16">
              <DomainInput v-model="formDomains" :max="30" ref="domainInput" />

              <p class="hint-text mt-2">
                Enter the full URLs of the websites you want to crawl (up to 30). All discoverable pages will be listed once the crawl completes.
              </p>

              <CrawlOptions
                :depth.sync="formMaxDepth"
                :limit.sync="formPageLimit"
                :depth-mode.sync="formDepthMode"
                :url-report-mode.sync="formUrlReportMode"
                :col-span="12"
              />

              <a-form-item label="Email Notification Contact Group">
                <a-select v-model="formContactGroupId" size="large" placeholder="Select contact group" showSearch :filterOption="filterOption">
                  <a-select-option :value="null">No email notifications</a-select-option>
                  <a-select-option v-for="cg in contactGroups" :key="cg.value" :value="cg.value">
                    {{ cg.label }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>
        </transition>
      </div>
      <div class="a-footer">
        <a-button key="back" size="large" class="mr-3 slide-down-enter-active">
          <router-link to="/web-crawl/history">Cancel</router-link>
        </a-button>
        <a-button
          type="primary"
          size="large"
          html-type="submit"
          class="slide-down-enter-active"
          :loading="isSubmitting"
        >
          Start Crawl
        </a-button>
      </div>
    </a-form>
  </div>
</template>

<script>
  import WebCrawlService from '../services/web-crawl.service';
  import { formatApiError } from '../helpers/format-api-error';
  import {
    clampCrawlDepth,
    clampPageLimit,
    createIdempotencyKey,
    normalizeUrlForDedup,
    DEFAULT_DEPTH,
    DEFAULT_PAGE_LIMIT,
  } from '../helpers/crawl-validation';
  import ContactGroupService from '@modules/ContactGroup/services/contact-group.service';
  import DomainInput from './DomainInput.vue';
  import CrawlOptions from './CrawlOptions.vue';
  import {
    DEFAULT_DEPTH_MODE,
    DEFAULT_URL_REPORT_MODE,
  } from '../consts/CrawlStatus.consts';

  export default {
    name: 'CrawlForm',
    components: { DomainInput, CrawlOptions },
    data() {
      return {
        formDomains: [{ url: '' }],
        formMaxDepth: DEFAULT_DEPTH,
        formPageLimit: DEFAULT_PAGE_LIMIT,
        formDepthMode: DEFAULT_DEPTH_MODE,
        formUrlReportMode: DEFAULT_URL_REPORT_MODE,
        isSubmitting: false,
        contactGroups: [],
        formContactGroupId: null,
        /** Set once per submit attempt; reused if submit fails so retries map to same crawl. Cleared on success or when leaving the form. */
        crawlIdempotencyKey: null,
      };
    },
    mounted() {
      this.fetchContactGroups();
    },
    beforeDestroy() {
      this.crawlIdempotencyKey = null;
    },
    methods: {
      async fetchContactGroups() {
        try {
          const groups = await ContactGroupService.getList();
          const list = groups || [];
          const defaultGroup = list.find((g) => g.description === 'DEFAULT');
          this.contactGroups = list.map((g) => ({ label: g.description, value: g.id }));
          if (defaultGroup) {
            this.formContactGroupId = defaultGroup.id;
          }
        } catch {
          this.$showErrorMessage('Failed to load contact groups');
        }
      },
      filterOption(input, option) {
        return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
      },
      async onSubmit() {
        if (this.isSubmitting) return;
        const validationError = this.$refs.domainInput.validateAll();
        if (validationError) {
          this.$showErrorMessage(validationError);
          return;
        }
        const maxDepth = clampCrawlDepth(this.formMaxDepth);
        const limit = clampPageLimit(this.formPageLimit);
        if (maxDepth !== this.formMaxDepth) this.formMaxDepth = maxDepth;
        if (limit !== this.formPageLimit) this.formPageLimit = limit;
        if (!this.crawlIdempotencyKey) {
          this.crawlIdempotencyKey = createIdempotencyKey();
        }
        this.isSubmitting = true;
        try {
          const seen = new Set();
          const uniqueDomains = this.formDomains
            .map((d) => d.url.trim())
            .filter(Boolean)
            .filter((url) => {
              const key = normalizeUrlForDedup(url);
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
          const payload = {
            domains: uniqueDomains,
            max_depth: maxDepth,
            depth_mode: this.formDepthMode,
            limit,
            url_report_mode: this.formUrlReportMode,
            contact_group_id: this.formContactGroupId,
          };
          const response = await WebCrawlService.submitCrawl(payload, {
            idempotencyKey: this.crawlIdempotencyKey,
          });
          this.crawlIdempotencyKey = null;
          this.$showSuccessMessage('Your crawl request has been submitted.');
          if (response && response.crawl_request_id) {
            this.$router.push(`/web-crawl/${response.crawl_request_id}`);
          } else {
            this.$router.push('/web-crawl/history');
          }
        } catch (error) {
          this.$showErrorMessage(formatApiError(error, 'Failed to submit crawl request.'));
        } finally {
          this.isSubmitting = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .hint-text {
    color: $prLgt;
    font-size: 13px;
  }
</style>
